import React from 'react';

/**
 * Direction A — Drafting primitives
 *
 * Reusable SVG building blocks for the technical drawings on the plate
 * sheet. Every line is stroked at exactly 1 user unit, and the SVGs
 * are rendered with `vector-effect="non-scaling-stroke"` so that the
 * hairline stays 1 device pixel regardless of viewBox scaling. This is
 * the "ruling engine" that the challenger #4 raise donated to the
 * assigned direction: rules survive resize and zoom without pixel drift.
 *
 * All primitives accept a `tone` prop that flips between ink and
 * rubric so that selection state colors the WHOLE drawing (not just
 * an outline) at page scale.
 */

export type Tone = 'ink' | 'rubric' | 'muted';

const toneColor = (tone: Tone) =>
  tone === 'rubric' ? '#77B72B' : tone === 'muted' ? '#4A4E55' : '#3A3C3E';

const HAIRLINE = {
  vectorEffect: 'non-scaling-stroke' as const,
  strokeLinecap: 'butt' as const,
  strokeLinejoin: 'miter' as const,
};

/** A dimension chain: two witness lines, one dimension line with tick
 *  marks at each end (draftsman "serif" style), and a centred numeric
 *  label positioned above the dimension line. */
export function DimensionChain({
  x1,
  x2,
  y,
  offset = 12,
  label,
  tone = 'ink',
  labelSide = 'above',
}: {
  x1: number;
  x2: number;
  y: number;
  offset?: number;
  label: string;
  tone?: Tone;
  labelSide?: 'above' | 'below';
}) {
  const c = toneColor(tone);
  const yDim = y + (labelSide === 'above' ? -offset : offset);
  const tick = 4;
  const labelY = yDim + (labelSide === 'above' ? -6 : 14);
  return (
    <g>
      {/* witness lines */}
      <line
        x1={x1}
        y1={y}
        x2={x1}
        y2={yDim + (labelSide === 'above' ? -3 : 3)}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={x2}
        y1={y}
        x2={x2}
        y2={yDim + (labelSide === 'above' ? -3 : 3)}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      {/* dimension line */}
      <line
        x1={x1}
        y1={yDim}
        x2={x2}
        y2={yDim}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      {/* end ticks (45° drafting tick) */}
      <line
        x1={x1 - tick}
        y1={yDim + tick}
        x2={x1 + tick}
        y2={yDim - tick}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={x2 - tick}
        y1={yDim + tick}
        x2={x2 + tick}
        y2={yDim - tick}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <text
        x={(x1 + x2) / 2}
        y={labelY}
        fill={c}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={10}
        textAnchor="middle"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {label}
      </text>
    </g>
  );
}

/** Vertical version of DimensionChain. */
export function DimensionChainV({
  y1,
  y2,
  x,
  offset = 12,
  label,
  tone = 'ink',
  labelSide = 'left',
}: {
  y1: number;
  y2: number;
  x: number;
  offset?: number;
  label: string;
  tone?: Tone;
  labelSide?: 'left' | 'right';
}) {
  const c = toneColor(tone);
  const xDim = x + (labelSide === 'left' ? -offset : offset);
  const tick = 4;
  const labelX = xDim + (labelSide === 'left' ? -6 : 6);
  return (
    <g>
      <line
        x1={x}
        y1={y1}
        x2={xDim + (labelSide === 'left' ? -3 : 3)}
        y2={y1}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={x}
        y1={y2}
        x2={xDim + (labelSide === 'left' ? -3 : 3)}
        y2={y2}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={xDim}
        y1={y1}
        x2={xDim}
        y2={y2}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={xDim - tick}
        y1={y1 - tick}
        x2={xDim + tick}
        y2={y1 + tick}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <line
        x1={xDim - tick}
        y1={y2 - tick}
        x2={xDim + tick}
        y2={y2 + tick}
        stroke={c}
        strokeWidth={1}
        {...HAIRLINE}
      />
      <text
        x={labelX}
        y={(y1 + y2) / 2}
        fill={c}
        fontFamily="'Config Condensed', sans-serif"
        fontSize={10}
        textAnchor={labelSide === 'left' ? 'end' : 'start'}
        dominantBaseline="central"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {label}
      </text>
    </g>
  );
}

/** Wood-hatch fill for cross-section Aufsatzkranz. */
export function WoodHatchPattern({ id, tone = 'ink' }: { id: string; tone?: Tone }) {
  const c = toneColor(tone);
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="5" stroke={c} strokeWidth={0.9} opacity={0.85} {...HAIRLINE} />
      </pattern>
    </defs>
  );
}

/** Concrete/masonry cross-hatch. */
export function ConcreteHatchPattern({ id, tone = 'ink' }: { id: string; tone?: Tone }) {
  const c = toneColor(tone);
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="4" height="4">
        <circle cx="1" cy="1" r="0.5" fill={c} opacity={0.7} />
        <circle cx="3" cy="3" r="0.5" fill={c} opacity={0.7} />
      </pattern>
    </defs>
  );
}

/** Center-mark used on circular products. */
export function CenterMark({
  cx,
  cy,
  size = 8,
  tone = 'ink',
}: {
  cx: number;
  cy: number;
  size?: number;
  tone?: Tone;
}) {
  const c = toneColor(tone);
  return (
    <g>
      <line x1={cx - size} y1={cy} x2={cx + size} y2={cy} stroke={c} strokeWidth={1} {...HAIRLINE} />
      <line x1={cx} y1={cy - size} x2={cx} y2={cy + size} stroke={c} strokeWidth={1} {...HAIRLINE} />
    </g>
  );
}
